import { ChakraProvider } from '@chakra-ui/react';
import { css, Global } from '@emotion/react';
import { getAsset } from '@pelicargo/tenants';
import { SaasProvider } from '@saas-ui/react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import { TRPC } from './components/TRPC';
import { ENV } from './config/env';
import { AuthProvider } from './context/AuthProvider';
import { ConnectionProvider } from './context/ConnectionProvider';
import { DashboardProvider } from './context/DashboardContext';
import { Router } from './Router';
import { theme } from './theme';

const APP_ENV = ENV.APP_ENV;

Sentry.init({
  dsn: ENV.SENTRY_DSN,
  environment: ENV.APP_ENV,
  enabled: APP_ENV === 'stg' || APP_ENV === 'prd',
  integrations: [Sentry.browserTracingIntegration({}), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const GlobalStyles = css`
  :focus,
  :focus-visible,
  *[data-focus] {
    box-shadow: none !important;
    outline: none !important;
  }

  // Ensure the app is always at least the height of the screen
  body {
    min-height: 100vh;
    overflow-y: hidden;
  }
`;

const App = () => {
  return (
    <HelmetProvider>
      <Helmet>
        <link rel="icon" id="favicon" type="image/svg+xml" href={getAsset('favicon.svg')} />
      </Helmet>
      <DashboardProvider>
        <Router />
      </DashboardProvider>
    </HelmetProvider>
  );
};

createRoot(document.getElementById('root') as HTMLElement).render(
  <SaasProvider
    theme={theme}
    toastOptions={{
      defaultOptions: {
        position: 'bottom-right',
        size: 'sm',
        duration: 3000,
        isClosable: true,
      },
    }}
  >
    <ChakraProvider
      resetCSS
      theme={theme}
      toastOptions={{
        defaultOptions: {
          position: 'bottom-right',
          size: 'sm',
          duration: 3000,
          isClosable: true,
        },
      }}
    >
      <Global styles={GlobalStyles} />
      <ConnectionProvider>
        <BrowserRouter>
          <TRPC>
            <AuthProvider>
              <App />
            </AuthProvider>
          </TRPC>
        </BrowserRouter>
      </ConnectionProvider>
    </ChakraProvider>
  </SaasProvider>,
);
